import { ChangeEvent, DragEvent, MouseEvent, forwardRef, useCallback, useEffect, useRef, useState } from "react";

import { Button, CloseButtonAppearance, Icon, IconColor, IconName, LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { VALIDATION } from "../../constants/validation";
import { useId } from "../../hooks/useId";
import { createComponentStyleHook } from "../../tokens";
import { EmptyArray } from "../../utilities/EmptyArray";
import { formatBytes } from "../../utilities/fileSize";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { ValidityGroup, useValidity } from "../ValidityGroup";
import { InputContainer } from "../shared";
import { FileEntry } from "./FileEntry";
import { FileUploadProps } from "./FileUpload";
import { Placeholder } from "./Placeholder";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedFileUploadStyles = createComponentStyleHook<InputProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "file_upload",
});

/**
 * The `Input` component properties.
 */
export interface InputProps extends FileUploadProps {
  /**
   * Indicates if the file upload is required.
   */
  readonly required?: boolean;
}

/**
 * Determines whether the specified file has a valid size.
 * @param file The file to test.
 * @param maxSize The maximum file size.
 * @returns True if the file size does not exceed the maximum specified size, otherwise false.
 */
function isFileSizeValid(file: File, maxSize: number | undefined): boolean {
  return maxSize === undefined || (file.size ?? 0) <= maxSize;
}

/**
 * Determines whether the specified file has a valid extension.
 * @param file The file to test.
 * @param allowedExtensions The set of allowed file extensions.
 * @returns True if the file extension is valid, otherwise false.
 */
function isFileExtensionValid(file: File, allowedExtensions: ReadonlyArray<string> | undefined): boolean {
  const extension = file.name.split(".").pop();
  if (extension === undefined) {
    return allowedExtensions === undefined;
  }

  return (
    allowedExtensions === undefined
    || allowedExtensions
      .map((ext) => (ext.startsWith(".") ? ext.substring(1).toLowerCase() : ext.toLowerCase()))
      .includes(extension.toLowerCase())
  );
}

/**
 * A file upload component.
 */
export const Input = forwardRef<HTMLDivElement, InputProps>(function Input(props, ref) {
  const {
    label,
    required = false,
    tooltip,
    hideLabel = false,
    value = EmptyArray as ReadonlyArray<FileEntry>,
    allowMultiple = false,
    maxSize,
    maxFiles,
    allowedExtensions,
    onChange,
    onDelete,
    tabIndex = 0,
    onFocusEnter,
    onFocusExit,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
  } = props;

  const { mode, styles, StyleModeBoundary } = useResolvedFileUploadStyles(props);

  const [dropping, setDropping] = useState(false);
  const [hasExceededMaximumFileCount, setHasExceededMaximumFileCount] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const id = useId();
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const disabled = useInputDisabled(props);
  const [invalid] = useValidity();

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.FileUpload, "file-upload", { dropping, disabled }],
    testIdPrefix: "file-upload",
  });

  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const cancelHandler = useCallback(() => {
    if (uploadInputRef?.current?.files?.length === 0) {
      onFocusExit?.();
    }
  }, [uploadInputRef?.current]);

  useEffect(() => {
    uploadInputRef.current?.addEventListener("cancel", cancelHandler);

    return () => uploadInputRef.current?.removeEventListener("cancel", cancelHandler);
  }, [uploadInputRef?.current]);

  const uploadTargetClickHandler = useCallback(
    (ev: MouseEvent) => {
      const { button } = ev;
      if (button !== 0) return;
      if (disabled) return;

      uploadInputRef?.current?.click?.();
    },
    [disabled],
  );

  const handleKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        uploadInputRef?.current?.click?.();
      }
    },
    [disabled],
  );

  const processFiles = useCallback(
    (files: FileList): ReadonlyArray<FileEntry> | null => {
      if (files.length === 0) return value;

      setHasExceededMaximumFileCount(false);
      let result = value;

      if (allowMultiple) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (!file) continue;
          if (!isFileExtensionValid(file, allowedExtensions)) continue;
          if (maxFiles !== undefined && (result?.length ?? 0) >= maxFiles) {
            setHasExceededMaximumFileCount(true);
            continue;
          }

          result = [...(result || (EmptyArray as ReadonlyArray<FileEntry>)), { name: file.name, type: file.type, file }];
        }
      } else {
        if (maxFiles !== undefined && maxFiles < 1) {
          setHasExceededMaximumFileCount(true);
          return value;
        }

        const file = Array.from(files).find((file) => isFileExtensionValid(file, allowedExtensions));
        if (!file) return value;
        result = [{ name: file.name, type: file.type, file: file }];
      }

      return result;
    },
    [value, allowedExtensions, allowMultiple, maxFiles],
  );

  const fileChangeHandler = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      const { target } = ev;
      const { files } = target;

      if (!files) return;

      const newValue = processFiles(files);

      target.value = "";
      if (newValue === value) return;

      onChange?.(newValue, value);
    },
    [value, processFiles, onChange],
  );

  const removeAttachmentClickHandler = useCallback(
    async (entry: FileEntry) => {
      if (disabled) return;

      const { id } = entry;
      if (id !== undefined) {
        try {
          await onDelete?.(id);
        } catch {
          // show a message indicating that deletion failed, then quit out.
          return;
        }
      }

      const newValue = (value || EmptyArray).filter((o) => o !== entry);
      onChange?.(newValue, value);
      setHasExceededMaximumFileCount(false);

      // if field is required and the last file has been removed, show validation error
      onFocusExit?.();
    },
    [value, onDelete, onChange, disabled],
  );

  const dragEnterHandler = useCallback(
    (ev: DragEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
    },
    [disabled],
  );

  const dragOverHandler = useCallback(
    (ev: DragEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
      setDropping(true);
    },
    [allowMultiple, disabled],
  );

  const dragLeaveHandler = useCallback((ev: DragEvent) => {
    ev.stopPropagation();
    ev.preventDefault();
    setDropping(false);
  }, []);

  const dropHandler = useCallback(
    (ev: DragEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
      setDropping(false);

      if (disabled) return;

      const { dataTransfer } = ev;
      if (!dataTransfer) return;

      const { files } = dataTransfer;

      const newValue = processFiles(files);

      if (newValue === value) return;

      containerRef.current?.focus();
      onChange?.(newValue, value);
    },
    [value, processFiles, onChange, disabled],
  );

  const focusHandler = useCallback(() => {
    if (disabled) return;
    onFocusEnter?.();
  }, [disabled]);

  const blurHandler = useCallback(() => {
    if (disabled) return;
    onFocusExit?.();
  }, [disabled]);

  const refHandler = useCallback(
    (innerRef: HTMLDivElement | null) => {
      containerRef.current = innerRef;

      if (ref) {
        if (ref instanceof Function) {
          ref(innerRef);
        } else {
          ref.current = innerRef;
        }
      }
    },
    [ref],
  );

  let normalizedExtensions: ReadonlyArray<string> | undefined = undefined;
  if (allowedExtensions && allowedExtensions.length > 0) {
    normalizedExtensions = allowedExtensions.map((extension) => {
      if (extension.startsWith(".")) return extension;
      else return `.${extension}`;
    });
  }

  const acceptList = normalizedExtensions ? normalizedExtensions.join(",") : undefined;

  let extensionsMessage = "";
  if (normalizedExtensions && normalizedExtensions.length > 0) {
    extensionsMessage = normalizedExtensions.length === 1 ? "Supported file type is " : "Supported file types are ";
    extensionsMessage += normalizedExtensions.slice(0, -1).join(", ");

    if (normalizedExtensions.length > 1) {
      extensionsMessage += ` and `;
    }

    extensionsMessage += `${normalizedExtensions[normalizedExtensions.length - 1]}.`;
  }

  const fileSizeMessage = maxSize === undefined ? "" : `Max file size is ${formatBytes(maxSize)}.`;
  const fileCountMessage = maxFiles === undefined ? "" : `Maximum ${maxFiles} file${maxFiles === 1 ? "" : "s"}.`;
  const limitationMessage = [fileSizeMessage, extensionsMessage, fileCountMessage].filter(Boolean).join(" ") || undefined;

  return (
    <StyleModeBoundary mode={mode}>
      <LoadingGroup.Placeholder
        fallback={<Placeholder hasInstructions={!!limitationMessage} />}
      >
        <div className={classNames(baseHtmlProps.className)} style={baseHtmlProps.style}>
          <Stack testId={baseHtmlProps["data-testid"]}>
            <div
              className={classNames(styles.ControlArea, {
                [styles.ControlAreaDisabled]: disabled,
                [styles.ControlAreaInvalid]: invalid || hasExceededMaximumFileCount,
              })}
            >
              <ValidityGroup
                invalid={hasExceededMaximumFileCount}
                error={hasExceededMaximumFileCount ? VALIDATION.MAXIMUM_FILES : null}
              >
                <InputContainer
                  id={id}
                  label={label}
                  instructions={limitationMessage}
                  disabled={disabled}
                  readOnly={readOnly}
                  required={required}
                  tooltip={tooltip}
                  hideLabel={hideLabel}
                >
                  {!readOnly && (
                    <div
                      ref={refHandler}
                      className={classNames(styles.UploadTargetContainer, "upload-target-container")}
                      onClick={uploadTargetClickHandler}
                      onDragEnter={dragEnterHandler}
                      onDragOver={dragOverHandler}
                      onDragLeave={dragLeaveHandler}
                      onDrop={dropHandler}
                      onFocus={focusHandler}
                      onBlur={blurHandler}
                      tabIndex={tabIndex}
                      onKeyDown={handleKeyDown}
                      role="button"
                      aria-label={ariaLabelledBy ? undefined : ariaLabel || "upload file"}
                      aria-labelledby={ariaLabelledBy}
                    >
                      <Stack alignment="center" spacing="small">
                        <Icon name="upload" color={disabled ? "lightgrey" : "turquoise"} size="regular" aria-hidden="true" />
                        <Paragraph>Drag and drop file(s) here or click to select a file.</Paragraph>
                      </Stack>
                      <input
                        id={id}
                        ref={uploadInputRef}
                        type="file"
                        multiple={allowMultiple}
                        accept={acceptList}
                        onChange={fileChangeHandler}
                        disabled={disabled}
                        readOnly={readOnly || submitting}
                        hidden
                        aria-labelledby={ariaLabelledBy}
                        aria-describedby={ariaDescribedBy}
                        aria-live="polite"
                      />
                    </div>
                  )}
                  {readOnly && (!value || value.length === 0) && (
                    <div className={classNames(styles.Files, "files")}>
                      <Stack spacing="small">
                        <div className={classNames(styles.FileItem, "file")}>
                          <Stack spacing="none">
                            <Shelf spacing="small">
                              <Icon name="attachment" color="black" />
                              <span>No files attached</span>
                              <Shelf.Spacer />
                            </Shelf>
                          </Stack>
                        </div>
                      </Stack>
                    </div>
                  )}
                </InputContainer>
              </ValidityGroup>
            </div>
            {value && value.length > 0 && (
              <div className={classNames(styles.Files, "files")}>
                <Stack spacing="small">
                  {(value || (EmptyArray as ReadonlyArray<FileEntry>)).map((entry, index) => {
                    const fileSizeValid = entry.file ? isFileSizeValid(entry.file, maxSize) : true;
                    const valid = fileSizeValid;

                    let iconName: IconName = !valid ? "warning-circle" : "attachment";
                    let iconColor: IconColor = !valid ? "red" : "black";
                    if (disabled) {
                      iconName = "attachment";
                      iconColor = "lightgrey";
                    }

                    return (
                      <div key={index} className={classNames(styles.FileItem, "file", { [styles.FileItemInvalid]: !valid, invalid: !valid })}>
                        <Stack spacing="none">
                          <Shelf spacing="small">
                            <Icon name={iconName} color={iconColor} />
                            <span>{entry.name}</span>
                            <Shelf.Spacer />
                            {((onDelete && entry.id !== undefined) || entry.id === undefined) && !readOnly && (
                              <Button
                                icon={onDelete && entry.id !== undefined ? "delete" : "x"}
                                appearance={CloseButtonAppearance}
                                tooltip="Remove file"
                                size="small"
                                disabled={disabled}
                                onClick={() => removeAttachmentClickHandler(entry)}
                              />
                            )}
                          </Shelf>
                          {!valid && !disabled && (
                            <div className={classNames(styles.ErrorMessage, "error-message")}>
                              <Paragraph>File cannot be larger than {formatBytes(maxSize ?? 0)}.</Paragraph>
                            </div>
                          )}
                        </Stack>
                      </div>
                    );
                  })}
                </Stack>
              </div>
            )}
          </Stack>
          <div className={styles["sr-only"]} aria-live="polite" aria-atomic="true">
            {value && value.length > 0 ? `${value.length} file${value.length > 1 ? "s" : ""} selected` : ""}
          </div>
        </div>
      </LoadingGroup.Placeholder>
    </StyleModeBoundary>
  );
});
